import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSample } from "../SampleContext";
import { useLayout } from "../LayoutContext";

interface DeepfakeGeneratorProps {
    setScreen: (screen: "intro" | "recorder" | "uploader" | "webcam" | "form") => void;
}

const languages: { code: string; name: string }[] = [
    { code: 'en', name: 'English (US)' },
    { code: 'en', name: 'English (UK)' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'it', name: 'Italian' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' },
    { code: 'nl', name: 'Dutch' },
    { code: 'ru', name: 'Russian' },
    { code: 'tr', name: 'Turkish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'pl', name: 'Polish' },
    { code: 'hi', name: 'Hindi' },
];

const PIPELINE_STEPS = [
    { num: "1", label: "Cloning voice" },
    { num: "2", label: "Generating speech" },
    { num: "3", label: "Uploading assets" },
    { num: "4", label: "Rendering video" },
];

const isValidHexColor = (c: string): boolean => /^#([0-9A-F]{3}){1,2}$/i.test(c);

const DeepfakeGenerator: React.FC<DeepfakeGeneratorProps> = ({ setScreen }) => {
    const { msp, account } = useParams<{ msp?: string; account?: string }>();
    const { layout } = useLayout();
    const { recordedBlob, uploadedFiles, capturedImage, setRecordedBlob, setUploadedFiles, setCapturedImage, setSampleDuration } = useSample();

    const [name, setName] = useState("");
    const [languageName, setLanguageName] = useState("");
    const [languageCode, setLanguageCode] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pipelineStep, setPipelineStep] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [errors, setErrors] = useState<{ name?: string; language?: string }>({});
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const listDivRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<number | null>(null);

    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : "#22FFAF",
        text: isValidHexColor(layout.color.text) ? layout.color.text : "#000000",
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : "#1DE9A3",
    };

    const hasAudio = !!(uploadedFiles?.length || recordedBlob);
    const hasImage = !!capturedImage;

    const filteredLanguages = languages.filter((lang) =>
        lang.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleLanguageSelect = (lang: { code: string; name: string }) => {
        setLanguageCode(lang.code);
        setLanguageName(lang.name);
        setSearchQuery(lang.name);
        setIsDropdownOpen(false);
    };

    const validate = () => {
        const e: { name?: string; language?: string } = {};
        if (!name.trim()) e.name = "Name is required";
        if (!languageCode) e.language = "Please pick a language";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    useEffect(() => {
        validate();
    }, [name, languageCode]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                searchInputRef.current && listDivRef.current &&
                !searchInputRef.current.contains(e.target as Node) &&
                !listDivRef.current.contains(e.target as Node)
            ) setIsDropdownOpen(false);
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    const startPipelineAnimation = () => {
        setPipelineStep(0);
        setElapsedTime(0);
        timerRef.current = setInterval(() => {
            setElapsedTime((t) => t + 1);
        }, 1000) as unknown as number;
    };

    const stopPipelineAnimation = () => {
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        if (!hasAudio || !hasImage) return;

        setIsSubmitting(true);
        setSubmitError(null);
        startPipelineAnimation();

        try {
            const fd = new FormData();

            if (uploadedFiles && uploadedFiles.length > 0) {
                fd.append("mp3File", uploadedFiles[0], uploadedFiles[0].name || "upload.mp3");
            } else if (recordedBlob) {
                const ext = recordedBlob.type?.startsWith("video/") ? "webm"
                    : recordedBlob.type === "audio/wav" ? "wav" : "mp3";
                fd.append("mp3File", recordedBlob, `recording.${ext}`);
            }

            fd.append("imageFile", capturedImage!, "webcam.jpg");
            fd.append("name", name);
            fd.append("language", languageCode);
            fd.append("termsAccepted", String(termsAccepted));
            // Read email from URL query param (passed by landing page email gate)
            const urlParams = new URLSearchParams(window.location.search);
            const gateEmail = urlParams.get('email') || '';
            if (gateEmail) fd.append("email", gateEmail);
            if (msp?.trim()) fd.append("msp", msp);
            if (account?.trim()) fd.append("account", account);

            // Update pipeline steps based on elapsed time
            const stepInterval = setInterval(() => {
                setPipelineStep((prev) => {
                    if (prev < 3) return prev + 1;
                    clearInterval(stepInterval);
                    return prev;
                });
            }, 40000);

            const res = await fetch("/api/create-deepfake", {
                method: "POST",
                body: fd,
            });

            clearInterval(stepInterval);

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData?.details || errData?.error || `HTTP ${res.status}`);
            }

            const data = await res.json();
            if (!data?.success || !data?.videoUrl) throw new Error("Unexpected response");

            stopPipelineAnimation();
            setPipelineStep(4);
            setVideoUrl(data.videoUrl);

            setRecordedBlob(null);
            setUploadedFiles(null);
            setCapturedImage(null);
            setSampleDuration(0);
        } catch (err: any) {
            console.error("Deepfake submit error:", err);
            stopPipelineAnimation();
            setSubmitError(err.message || "Failed to generate deepfake video");
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetAll = () => {
        setVideoUrl(null);
        setSubmitError(null);
        setName("");
        setLanguageCode("");
        setLanguageName("");
        setSearchQuery("");
        setPipelineStep(0);
        setElapsedTime(0);
        setTermsAccepted(false);
        setRecordedBlob(null);
        setUploadedFiles(null);
        setCapturedImage(null);
        setSampleDuration(0);
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    // ── Loading / Result view ──

    if (isSubmitting || videoUrl || submitError) {
        const finished = !!videoUrl;

        return (
            <div style={{ fontFamily: "'Nunito Sans', sans-serif" }} className="w-full max-w-[440px] p-8 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center">
                <h1 className="text-[24px] font-extrabold mb-1" style={{ color: safeColors.buttons }}>
                    {finished ? "Your Deepfake is Ready" : submitError ? "Generation Failed" : "Generating Your Deepfake"}
                </h1>
                <p className="text-gray-500 text-sm mb-6 text-center" style={{ lineHeight: 1.5 }}>
                    {finished
                        ? "Your video has been generated successfully."
                        : submitError
                            ? submitError
                            : "This may take several minutes. Please don't close this page."
                    }
                </p>

                {/* Video area */}
                <div className="w-full mb-6 rounded-xl flex items-center justify-center overflow-hidden"
                    style={{
                        maxWidth: 340, aspectRatio: '16/10',
                        backgroundColor: '#111',
                        border: `1px solid ${finished ? safeColors.buttons : '#1a1a1a'}`,
                        transition: 'border-color 0.5s',
                    }}>
                    {finished ? (
                        <div className="relative w-full h-full">
                            <video
                                src={videoUrl!}
                                controls
                                autoPlay
                                controlsList="nodownload noremoteplayback"
                                disablePictureInPicture
                                onContextMenu={(e) => e.preventDefault()}
                                className="w-full h-full object-cover rounded-xl"
                            />
                            <div style={{
                                position: 'absolute',
                                top: 8, left: 0, right: 0,
                                textAlign: 'center',
                                color: 'rgba(255,255,255,0.35)',
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                pointerEvents: 'none',
                                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                                userSelect: 'none',
                            }}>BREACHER.AI DEMO</div>
                        </div>
                    ) : submitError ? (
                        <div className="text-center p-4">
                            <div className="text-4xl mb-2">⚠️</div>
                            <div className="text-red-400 text-sm">{submitError}</div>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div style={{
                                width: 40, height: 40,
                                border: `3px solid ${safeColors.buttons}`,
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 12px',
                            }} />
                            <div className="text-gray-600 text-xs">Rendering in progress...</div>
                        </div>
                    )}
                </div>

                {/* Pipeline steps */}
                {!finished && !submitError && (
                    <div className="w-full mb-4">
                        {PIPELINE_STEPS.map((step, i) => {
                            const active = i === pipelineStep;
                            const complete = i < pipelineStep;
                            return (
                                <div key={i} className="flex items-center gap-3 py-2"
                                    style={{ opacity: i <= pipelineStep ? 1 : 0.2, transition: 'opacity 0.4s' }}>
                                    <div className="flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
                                        style={{
                                            width: 24, height: 24,
                                            backgroundColor: complete || active ? safeColors.buttons : '#222',
                                            color: complete || active ? safeColors.text : '#555',
                                            transition: 'all 0.3s',
                                        }}>
                                        {complete ? "✓" : step.num}
                                    </div>
                                    <span className="text-sm" style={{
                                        fontWeight: active ? 700 : 400,
                                        color: active ? safeColors.buttons : complete ? '#aaa' : '#666',
                                        transition: 'color 0.3s',
                                    }}>{step.label}</span>
                                    {active && (
                                        <span className="ml-auto text-xs animate-pulse" style={{ color: safeColors.buttons }}>●</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!finished && !submitError && (
                    <div className="text-gray-600 text-xs">Elapsed: {formatTime(elapsedTime)}</div>
                )}

                {(finished || submitError) && (
                    <div className="flex gap-3 mt-2">
                        <button className="bg-[#222] hover:bg-[#3a3a3a] rounded-lg px-5 py-3 font-bold text-white text-sm"
                            onClick={() => { resetAll(); setScreen("intro"); }}>
                            Done
                        </button>
                        <button className="rounded-lg px-5 py-3 font-bold text-sm cursor-pointer"
                            style={{ backgroundColor: safeColors.buttons, color: safeColors.text }}
                            onClick={() => { resetAll(); setScreen("intro"); }}>
                            Create Another
                        </button>
                    </div>
                )}

                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ── Form view ──

    return (
        <div style={{ fontFamily: "'Nunito Sans', sans-serif" }} className="w-full max-w-[440px] p-8 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center">
            <h1 className="text-[24px] font-extrabold mb-1" style={{ color: safeColors.buttons }}>Confirm & Generate</h1>

            <div className="flex gap-2 mb-5 mt-2">
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#14532d', color: '#86efac' }}>✓ Voice</span>
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#14532d', color: '#86efac' }}>✓ Photo</span>
            </div>

            {/* Name */}
            <div className="mb-4 w-full">
                <label className="block text-sm text-gray-300 font-semibold">Name *</label>
                <input
                    type="text"
                    className="mt-1.5 p-2.5 w-full rounded-lg bg-[#151515] text-white placeholder:text-gray-500 outline-none text-sm"
                    style={{ border: `1px solid #333`, transition: 'border-color 0.2s' }}
                    placeholder="e.g., Alex Morgan"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={(e) => (e.currentTarget.style.borderColor = safeColors.buttons)}
                    onBlur={(e) => (e.currentTarget.style.borderColor = '#333')}
                />
            </div>

            {/* Language */}
            <div className="mb-5 w-full relative">
                <label className="block text-sm text-gray-300 font-semibold">Language *</label>
                <input
                    type="text"
                    ref={searchInputRef}
                    className="mt-1.5 p-2.5 w-full bg-[#151515] text-white placeholder:text-gray-500 outline-none text-sm"
                    style={{
                        border: '1px solid #333',
                        borderRadius: isDropdownOpen && filteredLanguages.length > 0 ? '8px 8px 0 0' : 8,
                        transition: 'border-color 0.2s',
                    }}
                    placeholder="Search…"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setLanguageCode(""); }}
                    onFocus={() => setIsDropdownOpen(true)}
                />
                <div
                    ref={listDivRef}
                    className={`absolute w-full max-h-40 overflow-y-auto bg-[#141414] z-20 ${isDropdownOpen && filteredLanguages.length > 0 ? "block" : "hidden"}`}
                    style={{ border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px' }}
                >
                    {filteredLanguages.map((lang, i) => (
                        <div
                            key={`${lang.code}-${i}`}
                            className="px-3 py-2 cursor-pointer text-gray-300 text-sm hover:text-black transition-colors"
                            style={{ transition: 'all 0.15s' }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = safeColors.buttons; e.currentTarget.style.color = safeColors.text; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#d1d5db'; }}
                            onClick={() => handleLanguageSelect(lang)}
                        >
                            {lang.name}
                        </div>
                    ))}
                </div>
            </div>

            {/* Terms of use */}
            <label className="flex items-start gap-3 mb-5 w-full cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 flex-shrink-0"
                    style={{ accentColor: safeColors.buttons, width: 16, height: 16 }}
                />
                <span className="text-xs text-gray-400 leading-relaxed">
                    I confirm I have consent to use this person's likeness and voice for
                    demonstration purposes only. I understand this tool is for authorized
                    security testing and awareness. Misuse may result in legal action.
                </span>
            </label>

            {/* Buttons */}
            <div className="flex gap-3">
                <button className="bg-[#222] hover:bg-[#3a3a3a] rounded-lg px-5 py-3 font-bold text-white text-sm"
                    onClick={() => setScreen("webcam")}>
                    ← Back
                </button>
                <button
                    className={`rounded-lg px-6 py-3 font-bold text-sm ${!name.trim() || !hasAudio || !hasImage || !languageCode || !termsAccepted ? 'opacity-35 cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{ backgroundColor: safeColors.buttons, color: safeColors.text }}
                    onClick={handleSubmit}
                    disabled={!name.trim() || !hasAudio || !hasImage || !languageCode || !termsAccepted}
                >
                    Generate Deepfake
                </button>
            </div>
        </div>
    );
};

export default DeepfakeGenerator;
